import { usePermissions } from './PermissionsContext'
import type { BrandScope } from '../api/queryKeys'

/**
 * The brand switcher's choice, for list pages to filter by.
 *
 * `brandId` is `undefined` for "All brands" — the shape every list call and query key takes, so a
 * page passes it straight through: `queryKeys.devices.map(brandId)`, `getMapDevices(brandId)`.
 *
 * Read defensively. Most page tests replace `usePermissions` with a mock that knows nothing of
 * brands, and some components render with no provider at all; either way a missing value means
 * what it would mean in the app: no brand chosen.
 */
export function useActiveBrand(): {
  brandId: BrandScope
  setActiveBrand: (brandId: string | null) => void
} {
  const permissions = useOptionalPermissions()
  return {
    brandId: permissions?.activeBrandId ?? undefined,
    setActiveBrand: permissions?.setActiveBrand ?? noop,
  }
}

/**
 * The brand a list should be filtered by, given the page's own brand filter if it has one.
 *
 * A brand chosen on the page is the more specific request and wins; a page left on "All brands"
 * follows the switcher. So the switcher scopes every page, and a page can still look at one brand.
 */
export function useBrandFilter(pageBrandId?: string | null): BrandScope {
  const { brandId } = useActiveBrand()
  return pageBrandId ? pageBrandId : brandId
}

function noop() {}

/**
 * `usePermissions()`, or nothing outside a provider.
 *
 * A component rendered on its own — a picker in a dialog's test, say — has no provider above it,
 * and "no brand chosen" is the right answer there rather than a crash. Safe as a hook: the context
 * read inside `usePermissions` happens on every call, before it can throw, so the hook order never
 * changes.
 */
function useOptionalPermissions(): Partial<ReturnType<typeof usePermissions>> | undefined {
  try {
    return usePermissions() as Partial<ReturnType<typeof usePermissions>> | undefined
  } catch {
    return undefined
  }
}
